import React from 'react';
import NextLink, { type LinkProps as NextLinkProps } from 'next/link';
import { cn } from 'lib/utils';
import { type IconNames } from 'atoms/Icon/Icons';
import { Icon } from 'ui';

type CommonProps = {
  icon: IconNames;
  label: string;
  className?: string;
  iconClassName?: string;
};

type AnchorOnlyProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'onClick' | 'className'
>;

type ButtonOnlyProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'className'
> & {
  type?: 'button' | 'submit' | 'reset';
};

type LinkVariantProps = CommonProps &
  AnchorOnlyProps &
  Omit<NextLinkProps, 'href'> & {
    href: NextLinkProps['href'];
    onClick?: never;
  };

type ButtonVariantProps = CommonProps &
  ButtonOnlyProps & {
    href?: never;
    disabled?: boolean;
  };

export type IconButtonProps = ButtonVariantProps | LinkVariantProps;

const IconButton = ({
  icon,
  label,
  className: classNameProp,
  iconClassName: iconClassNameProp,
  ...rest
}: IconButtonProps) => {
  const className = cn(
    'group shrink-0 size-12 flex items-center justify-center bg-clay-50/20 [&:hover,&:focus]:not-disabled:bg-clay-50/25 rounded-full transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed',
    classNameProp
  );
  const iconClassName = cn(
    'shrink-0 w-5 h-auto fill-clay-50',
    iconClassNameProp
  );

  if ('href' in rest) {
    const linkProps = rest as LinkVariantProps;

    return (
      <NextLink {...{ className }} {...linkProps} aria-label={label}>
        <Icon name={icon} className={iconClassName} />
      </NextLink>
    );
  }

  const {
    onClick,
    type = 'button',
    disabled,
    ...buttonProps
  } = rest as ButtonVariantProps;

  return (
    <button
      {...{ className, onClick, type, disabled }}
      {...buttonProps}
      aria-label={label}
    >
      <Icon name={icon} className={iconClassName} />
    </button>
  );
};

export default IconButton;
